import {
  type Branch,
  type CreateLotInput,
  INGESTION_SOURCE_LABELS,
  LOT_STATUS_LABELS,
  type Lot,
  type LotStatus,
  type Page as PageDto,
  PERMISSIONS,
} from '@edms/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageBody, PageHeader } from '@/components/page-header';
import { type Column, DataTable, Pagination } from '@/components/ui/data-table';
import { Dialog, useToast } from '@/components/ui/overlays';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { Badge, EmptyState, ProgressBar, type Tone } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { formatDate, formatNumber, formatRelative } from '@/lib/utils';

export const LOT_STATUS_TONES: Record<LotStatus, Tone> = {
  DRAFT: 'neutral',
  SCANNING: 'info',
  OCR_RUNNING: 'info',
  VALIDATION_REQUIRED: 'warning',
  ARCHIVED: 'success',
  ERROR: 'danger',
};

/**
 * Lots de numérisation (§5).
 *
 * Écran de travail du Branch Leader : il y ouvre un lot le matin, y dépose ses
 * numérisations, en suit l'avancement et l'archive en fin de journée.
 */
export function LotsPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const branchId = searchParams.get('branchId') ?? '';
  const status = searchParams.get('status') ?? '';

  const { data: branches } = useQuery({
    queryKey: ['branches', 'selectable'],
    queryFn: () => api.get<Branch[]>('/branches/selectable'),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['lots', { branchId, status, page, pageSize }],
    queryFn: () =>
      api.get<PageDto<Lot>>('/lots', {
        page,
        pageSize,
        branchId: branchId || undefined,
        status: status || undefined,
      }),
    placeholderData: keepPreviousData,
    // Un lot en traitement évolue en tâche de fond : sans rafraîchissement, la
    // liste afficherait un état périmé jusqu'au prochain clic.
    refetchInterval: 15_000,
  });

  const setFilter = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const columns: readonly Column<Lot>[] = [
    {
      key: 'reference',
      header: 'Référence',
      width: '18%',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-mono text-sm font-medium text-ink">{row.reference}</span>
          <span className="text-2xs text-ink-faint">
            {INGESTION_SOURCE_LABELS[row.source]} · {row.operatorName}
          </span>
        </div>
      ),
    },
    {
      key: 'branch',
      header: 'Bureau',
      width: '14%',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-2xs text-ink-faint">{row.branchCode}</span>
          <span className="truncate text-sm text-ink-muted">{row.branchName}</span>
        </div>
      ),
    },
    {
      key: 'scanDate',
      header: 'Date',
      width: '9%',
      render: (row) => <span className="text-sm text-ink-muted">{formatDate(row.scanDate)}</span>,
    },
    {
      key: 'receipts',
      header: 'Reçus',
      align: 'right',
      width: '7%',
      render: (row) => <span className="text-sm text-ink">{formatNumber(row.receiptCount)}</span>,
    },
    {
      key: 'pages',
      header: 'Pages',
      align: 'right',
      width: '7%',
      hideOnNarrow: true,
      render: (row) => <span className="text-sm text-ink-muted">{formatNumber(row.pageCount)}</span>,
    },
    {
      key: 'exceptions',
      header: 'Exceptions',
      align: 'center',
      width: '12%',
      render: (row) => {
        const total = row.pendingReviewCount + row.duplicateCount;
        if (total === 0) return <span className="text-xs text-ink-faint">—</span>;
        return (
          <div className="flex items-center justify-center gap-1">
            {row.pendingReviewCount > 0 && (
              <Badge tone="warning">{row.pendingReviewCount} à valider</Badge>
            )}
            {row.duplicateCount > 0 && <Badge tone="warning">{row.duplicateCount} doublon(s)</Badge>}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Statut',
      width: '16%',
      render: (row) => (
        <div className="space-y-1.5">
          <Badge tone={LOT_STATUS_TONES[row.status]} dot>
            {LOT_STATUS_LABELS[row.status]}
          </Badge>
          {(row.status === 'SCANNING' || row.status === 'OCR_RUNNING') && row.receiptCount > 0 && (
            <ProgressBar
              value={
                ((row.receiptCount - row.pendingReviewCount - row.duplicateCount) /
                  row.receiptCount) *
                100
              }
              tone="info"
            />
          )}
        </div>
      ),
    },
    {
      key: 'updated',
      header: 'Activité',
      align: 'right',
      width: '10%',
      hideOnNarrow: true,
      render: (row) => (
        <span className="text-xs text-ink-faint">{formatRelative(row.updatedAt)}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Numérisation"
        description="Lots de numérisation, avancement du traitement et archivage"
        actions={
          can(PERMISSIONS.LOTS_CREATE) && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Nouveau lot
            </Button>
          )
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Select
              size="sm"
              value={branchId}
              onChange={(event) => setFilter('branchId', event.target.value)}
              aria-label="Filtrer par bureau"
              className="w-56"
            >
              <option value="">Tous les bureaux</option>
              {branches?.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                </option>
              ))}
            </Select>

            <Select
              size="sm"
              value={status}
              onChange={(event) => setFilter('status', event.target.value)}
              aria-label="Filtrer par statut"
              className="w-52"
            >
              <option value="">Tous les statuts</option>
              {(Object.keys(LOT_STATUS_LABELS) as LotStatus[]).map((value) => (
                <option key={value} value={value}>
                  {LOT_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      <PageBody>
        <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            onRowClick={(row) => navigate(`/numerisation/lots/${row.id}`)}
            empty={
              <EmptyState
                icon={Layers}
                title="Aucun lot"
                description="Créez un lot pour commencer une session de numérisation."
                action={
                  can(PERMISSIONS.LOTS_CREATE) && (
                    <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                      Nouveau lot
                    </Button>
                  )
                }
              />
            }
          />

          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </div>
      </PageBody>

      <CreateLotDialog
        open={creating}
        branches={branches ?? []}
        onClose={() => setCreating(false)}
        onCreated={(lot) => navigate(`/numerisation/lots/${lot.id}`)}
      />
    </>
  );
}

function CreateLotDialog({
  open,
  branches,
  onClose,
  onCreated,
}: {
  open: boolean;
  branches: readonly Branch[];
  onClose: () => void;
  onCreated: (lot: Lot) => void;
}): React.JSX.Element {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [branchId, setBranchId] = useState('');
  const [notes, setNotes] = useState('');
  const [scanDate, setScanDate] = useState(() => new Date().toISOString().slice(0, 10));

  const create = useMutation({
    mutationFn: (input: CreateLotInput) => api.post<Lot>('/lots', input),
    onSuccess: (lot) => {
      void queryClient.invalidateQueries({ queryKey: ['lots'] });
      toast.success('Lot créé', `Référence ${lot.reference}`);
      setNotes('');
      onClose();
      onCreated(lot);
    },
    onError: (error) => {
      toast.error(
        'Création impossible',
        error instanceof ApiError ? error.message : 'Erreur inattendue.',
      );
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nouveau lot de numérisation"
      description="Le lot regroupe une session de numérisation et son historique de traitement."
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            variant="primary"
            loading={create.isPending}
            onClick={() =>
              create.mutate({
                source: 'WEB_UPLOAD',
                ...(branchId ? { branchId } : {}),
                ...(scanDate ? { scanDate } : {}),
                ...(notes.trim() ? { notes: notes.trim() } : {}),
              })
            }
          >
            Créer le lot
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Bureau"
          htmlFor="lot-branch"
          hint={
            user?.scope === 'OWN_BRANCH'
              ? `Les documents sont archivés par votre bureau (${user.branchCode}).`
              : undefined
          }
        >
          <Select id="lot-branch" value={branchId} onChange={(event) => setBranchId(event.target.value)}>
            <option value="">
              {user ? `Mon bureau — ${user.branchCode}` : 'Bureau de rattachement'}
            </option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.code} — {branch.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Date de numérisation" htmlFor="lot-date">
          <Input
            id="lot-date"
            type="date"
            value={scanDate}
            onChange={(event) => setScanDate(event.target.value)}
          />
        </Field>

        <Field label="Note" htmlFor="lot-notes" hint="Facultatif — visible dans l'historique du lot.">
          <Textarea
            id="lot-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Livraisons du 12 juillet, tournée nord…"
            maxLength={500}
          />
        </Field>
      </div>
    </Dialog>
  );
}
